import React, { useState, useEffect } from 'react';
import { TrendingUp, ShoppingCart, Eye, Star, ChevronLeft, ChevronRight, Flame } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import OptimizedImage from './OptimizedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrendingProducts = ({ onAddToCart, onQuickView }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/recommendations/trending?limit=10&days=7`)
      .then(res => res.json())
      .then(data => setProducts(data.products || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction * 280, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section className="py-10 bg-white dark:bg-gray-900" data-testid="trending-loading">
        <div className="max-w-7xl mx-auto px-4">
          <Skeleton className="h-7 w-48 mb-6" />
          <div className="flex gap-4 overflow-hidden">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="w-56 h-72 rounded-xl flex-shrink-0" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="py-10 bg-white dark:bg-gray-900" data-testid="trending-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Trending Now</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Popular products this week</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => scroll(-1)} className="rounded-full w-9 h-9 p-0" data-testid="trending-scroll-left">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => scroll(1)} className="rounded-full w-9 h-9 p-0" data-testid="trending-scroll-right">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product, idx) => {
            const discount = product.sale_price
              ? Math.round((1 - product.sale_price / product.price) * 100)
              : 0;
            const displayPrice = product.sale_price || product.price;

            return (
              <div
                key={product.id}
                className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 flex-shrink-0 w-56 snap-start"
                data-testid={`trending-product-${product.id}`}
              >
                {/* Rank badge */}
                {idx < 3 && (
                  <div className={`absolute top-2 left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-md ${
                    idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'
                  }`}>
                    #{idx + 1}
                  </div>
                )}
                {discount > 0 && (
                  <Badge className="absolute top-2 right-2 z-10 bg-red-500 text-white text-xs">-{discount}%</Badge>
                )}

                <div
                  className="aspect-square bg-gray-50 dark:bg-gray-700 cursor-pointer overflow-hidden"
                  onClick={() => onQuickView?.({
                    ...product,
                    image: product.images?.[0] || product.image || '/placeholder.png',
                    price: displayPrice,
                    originalPrice: product.sale_price ? product.price : null,
                    rating: product.rating || 4.5,
                    reviewCount: product.review_count || 0,
                    inStock: (product.stock_quantity || 0) > 0,
                  })}
                >
                  <OptimizedImage
                    src={product.images?.[0] || product.image || ''}
                    alt={product.name}
                    className="w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="p-3">
                  <p className="text-xs text-teal-600 dark:text-teal-400 font-medium mb-1">{product.brand || product.category || ''}</p>
                  <h3 className="text-sm font-medium text-gray-800 dark:text-white line-clamp-2 mb-2">{product.name}</h3>
                  
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{product.rating || '4.5'}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-base font-bold text-gray-800 dark:text-white">${displayPrice?.toFixed(2)}</span>
                      {discount > 0 && (
                        <span className="text-xs text-gray-400 line-through ml-1">${product.price?.toFixed(2)}</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="h-8 w-8 p-0 bg-teal-600 hover:bg-teal-700"
                      onClick={() => onAddToCart?.({
                        ...product,
                        image: product.images?.[0] || product.image,
                        price: displayPrice,
                      })}
                      data-testid={`trending-add-cart-${product.id}`}
                    >
                      <ShoppingCart className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrendingProducts;
